// ─────────────────────────────────────────────────────────────────────────────
//  Frontend license helpers — reads from localStorage (set by socket on login)
// ─────────────────────────────────────────────────────────────────────────────
import Swal from 'sweetalert2';
import axios from 'axios';
import { aiStatusUrl } from '../BaseURL';

/**
 * Check if a license type is allowed.
 * type: 'ai' | 'call_chat'
 */
export const isLicenseAllowed = (type) => {
    try {
        const stored = localStorage.getItem(`license_${type}`);
        if (!stored) return true; // default allow if not yet set
        return JSON.parse(stored).allowed !== false;
    } catch {
        return true;
    }
};

/**
 * Show a blocked popup for a license type.
 * type: 'ai' | 'call_chat'
 */
export const showLicenseBlocked = (type = 'ai') => {
    const label  = type === 'ai' ? 'AI Features' : 'Call & Chat';
    const reason = (() => {
        try {
            return JSON.parse(localStorage.getItem(`license_${type}`) || '{}').reason || '';
        } catch { return ''; }
    })();

    Swal.fire({
        icon:               'error',
        title:              `🔒 ${label} License Expired`,
        html: `
            <div style="text-align:center; padding:8px 0">
                <p style="color:#64748b; font-size:14px; margin-bottom:8px">
                    ${reason || `Your ${label} license has expired.`}
                </p>
                <p style="color:#94a3b8; font-size:12px">
                    Please contact TotalCare support to renew.
                </p>
            </div>
        `,
        confirmButtonText:  'OK',
        confirmButtonColor: '#d33',
    });
};

/**
 * Re-read the AI licence from the server (/ai/status) and store it where every
 * screen already looks (localStorage.license_ai). One rule for every AI button:
 * the facility's central ai_license row, plus a configured provider.
 * Keeps the last stored answer if the request fails, so a network blip does not
 * flip buttons on and off; the server still refuses unlicensed calls regardless.
 */
export const refreshAiLicense = async (fresh = false) => {
    try {
        const { data } = await axios.get(aiStatusUrl, { params: fresh ? { refresh: 1 } : {}, timeout: 15000 });
        if (data && typeof data.allowed === 'boolean') {
            localStorage.setItem('license_ai', JSON.stringify({
                allowed: data.allowed, reason: data.reason || null,
                provider: data.provider || null, model: data.model || null,
            }));
        }
        return data;
    } catch {
        return null;
    }
};

let _aiWatch = null;
/** Refresh now and every five minutes while the app is open. Safe to call twice. */
export const startAiLicenseWatch = () => {
    refreshAiLicense();
    if (_aiWatch) return;
    _aiWatch = setInterval(() => { refreshAiLicense(); }, 5 * 60 * 1000);
};

/**
 * Clear license storage on logout
 */
export const clearLicenseStorage = () => {
    localStorage.removeItem('license_ai');
    localStorage.removeItem('license_call_chat');
};